import { createMemo, createSignal } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import { annualDistribution, isCovered, isPriced, price as instrumentPrice } from '~data';
import { ACCOUNT_SHAPE, CONTRIBUTION_LIMITS, DEFAULT_LOAN_RATE, LOAN_KIND_LABEL, type Account, type Holding, type Loan, type Portfolio } from '~/data/portfolios';
import { sumOrNull, todayISO } from '~/lib/format';
import { db, debouncedWrite, reportError } from '~/lib/db';
import { loadTransactions, transactions } from '~/stores/transactions-store';
import { cashFlow, positionAt, positions, positionsIn, realised, type Position } from '~/stores/positions';
import { receivedIn } from '~/stores/received';

// The book lives in Supabase. `load()` fills the store from it; every writer
// updates the store first (so typing stays instant) and then persists on a
// debounce keyed to the individual field.
//
// Positions are NOT stored. A holding is the sum of the buys that created it,
// derived from the transactions blotter — there is no way for the two to drift
// apart because there is only one of them.

const [portfolios, setPortfolios] = createStore<Portfolio[]>([]);
const [activeId, setActiveId] = createSignal('');
const [loading, setLoading] = createSignal(true);

export { portfolios, activeId, setActiveId, loading };

const EMPTY: Portfolio = { id: '', label: '', accounts: [] };

// --- the shape, before it is a row ------------------------------------------

/**
 * Nothing in a book is stored until it is written to, so every id the app holds
 * is one of two things: a uuid for a row that exists, or a placeholder standing
 * in for one that does not.
 *
 * A placeholder is `<owner>:<a|l>:<kind>` — `main:a:tfsa`, `sandbox:l:heloc`, or
 * a plan's uuid in place of the slot name. That makes it unique across
 * portfolios, stable across reloads, and distinguishable from a uuid by the
 * colons, which a uuid cannot contain. Every read path treats an id as an opaque
 * string and so needs no changes; the writers call materialise() first and swap
 * in the real one.
 *
 * The `a`/`l` is not decoration: `margin` is both an account kind and a loan
 * kind, so owner and kind together do not identify a row.
 */
type Sort = 'a' | 'l';

const placeholder = (owner: string, sort: Sort, kind: string): string => `${owner}:${sort}:${kind}`;

const isPlaceholder = (id: string): boolean => id.includes(':');

/** What a placeholder stands for. Owner is a slot name or a plan's uuid, which is
    the distinction ensure_account draws between its two ways of being told where
    to put the row. */
const parsePlaceholder = (id: string): { owner: string; sort: Sort; kind: string } => {
    const [owner, sort, kind] = id.split(':');
    return { owner: owner ?? '', sort: (sort ?? 'a') as Sort, kind: kind ?? '' };
};

/** The four accounts, with the stored ones filled in over the top. Order comes
    from the constant, not from the database, so it does not shuffle as rows
    appear. */
const accountsFor = (owner: string, stored: Account[]): Account[] =>
    ACCOUNT_SHAPE.map((shape) => {
        const row = stored.find((a) => a.kind === shape.kind);
        return row ?? { id: placeholder(owner, 'a', shape.kind), kind: shape.kind, label: shape.label, sheltered: shape.sheltered, deposit: 0 };
    });

/** The fixed workspaces, in the order they are shown. A plan sorts after them.
    An unstored slot uses its own name as its id, which is what ensure_slot takes
    to turn it into a row.

    Only the real book is fixed now. `sandbox` was here too, which is what made
    it undeletable in practice: anything in this list that has no stored row is
    synthesised again on the next load, so deleting the sandbox as a plan would
    have brought it straight back. A row already carrying the retired slot still
    loads from `stored` and sorts in with the plans, which is where it belongs. */
const SLOTS: { slot: NonNullable<Portfolio['slot']>; label: string }[] = [{ slot: 'main', label: 'Main' }];

const order = (p: Portfolio): number => {
    const at = SLOTS.findIndex((s) => s.slot === p.slot);
    return at === -1 ? SLOTS.length : at;
};

export const portfolio = createMemo<Portfolio>(() => portfolios.find((p) => p.id === activeId()) ?? portfolios[0] ?? EMPTY);

export const accounts = createMemo<Account[]>(() => portfolio().accounts);

/**
 * Your loans, and there is only one list of them.
 *
 * Not a property of the portfolio on screen. You do not have a different HELOC
 * when you are planning — it is one facility, at one rate, with one balance
 * owed — so the sandbox and every plan are measured against these rows rather
 * than against copies that go stale separately.
 *
 * They are also not rendered from a constant. Every other fixed set in this app
 * (the four accounts, the two slots) is fixed because the world is: a Canadian
 * holds at most one TFSA. Nothing caps how many lines of credit a person has,
 * so these are added, edited and deleted like the rows they are.
 */
const [loanRows, setLoanRows] = createStore<Loan[]>([]);

export const loans = createMemo<Loan[]>(() => loanRows.slice());

// --- loading ----------------------------------------------------------------

export const load = async (): Promise<void> => {
    setLoading(true);
    try {
        // One query per table rather than a wide join: the shape is nested, and
        // stitching four flat result sets is easier to follow than unflattening
        // one join. Four requests in parallel rather than one batch — PostgREST
        // has no batch — which costs connections, not round trips.
        //
        // None filter by user: row-level security scopes every one of them to
        // the session, so a forgotten clause cannot return someone else's book.
        const [p, a, l] = await Promise.all([
            db.from('portfolios').select('id, label, slot').order('position').order('label'),
            db.from('accounts').select('id, portfolio_id, kind, label, sheltered, deposit').order('position').order('label'),
            db.from('loans').select('id, kind, label, issuer, balance, credit_limit, rate').order('position').order('label'),
        ]);
        for (const res of [p, a, l]) if (res.error) throw new Error(res.error.message);

        const accountsByPortfolio = new Map<string, Account[]>();
        for (const r of a.data ?? []) {
            const id = String(r.id);
            const key = String(r.portfolio_id);
            const list = accountsByPortfolio.get(key) ?? [];
            list.push({
                id,
                kind: String(r.kind) as Account['kind'],
                label: String(r.label),
                sheltered: Number(r.sheltered) === 1,
                deposit: Number(r.deposit),
            });
            accountsByPortfolio.set(key, list);
        }

        setLoanRows(
            (l.data ?? []).map((r) => ({
                id: String(r.id),
                kind: String(r.kind) as Loan['kind'],
                label: String(r.label),
                issuer: r.issuer == null ? '' : String(r.issuer),
                balance: Number(r.balance),
                limit: r.credit_limit == null ? null : Number(r.credit_limit),
                rate: Number(r.rate),
            })),
        );

        const stored: Portfolio[] = (p.data ?? []).map((r) => {
            const id = String(r.id);
            const slot = r.slot == null ? undefined : (String(r.slot) as Portfolio['slot']);
            // A stored portfolio owns its rows by uuid; the accounts it has not
            // been written to yet are placeholders against that uuid.
            return {
                id,
                label: String(r.label),
                slot,
                accounts: accountsFor(id, accountsByPortfolio.get(id) ?? []),
            };
        });

        // The two fixed workspaces exist on screen whether or not they exist in
        // the database — that is the whole point of not seeding. A slot with no
        // row is synthesised here, addressed by its slot name, and becomes real
        // the first time something is written into it.
        const next: Portfolio[] = [
            ...SLOTS.filter((s) => !stored.some((x) => x.slot === s.slot)).map(
                (s): Portfolio => ({ id: s.slot, label: s.label, slot: s.slot, accounts: accountsFor(s.slot, []) }),
            ),
            ...stored,
        ].sort((a, b) => order(a) - order(b));

        setPortfolios(next);
        // Keep the current selection across a refresh where it still exists.
        if (!next.some((x) => x.id === activeId())) setActiveId(next[0]?.id ?? '');
    } catch (e) {
        reportError('loading portfolios', e);
    } finally {
        setLoading(false);
    }
};

// --- positions, summed from the blotter -------------------------------------

/**
 * Units and book cost per account and symbol.
 *
 * Walked rather than summed — see positions.ts. Book cost includes commissions,
 * which is what an adjusted cost base is, and a sale relieves the average cost
 * of the units sold rather than their proceeds.
 *
 * A closed-out position is dropped rather than shown at zero.
 */
export const accountHoldings = (a: Account): Holding[] => positionsIn(a.id).map((p) => ({ symbol: p.symbol, units: p.units, bookCost: p.bookCost }));

// --- resolving a holding against the pool -----------------------------------

/**
 * A holding with its money figures worked out.
 *
 * Units and book cost come from the blotter and are always known. Everything
 * that needs a market price or a distribution is null while the fund is
 * unpriced — the caller renders that as N/A rather than as zero.
 */
export interface ResolvedHolding {
    holding: Holding;
    symbol: string;
    accountId: string;
    sheltered: boolean;
    value: number | null;
    income: number | null;
    yieldPct: number | null;
    /** Yield measured against what the position actually cost. */
    yieldOnCost: number | null;
    gain: number | null;
}

export const resolve = (h: Holding, account: Account): ResolvedHolding | null => {
    if (!isCovered(h.symbol)) return null;

    const asOf = todayISO();
    const annual = annualDistribution(h.symbol, asOf);
    const unitPrice = instrumentPrice(h.symbol);
    const value = unitPrice == null ? null : h.units * unitPrice;
    const income = annual == null ? null : h.units * annual;

    return {
        holding: h,
        symbol: h.symbol,
        accountId: account.id,
        sheltered: account.sheltered,
        value,
        income,
        yieldPct: annual == null || unitPrice == null || unitPrice <= 0 ? null : (annual / unitPrice) * 100,
        yieldOnCost: income == null || h.bookCost <= 0 ? null : (income / h.bookCost) * 100,
        gain: value == null ? null : value - h.bookCost,
    };
};

// A holding whose symbol has fallen out of the covered pool is dropped rather
// than rendered as a zero — that is a data problem, not a $0 position.
export const resolveAccount = (a: Account): ResolvedHolding[] => accountHoldings(a).map((h) => resolve(h, a)).filter((r): r is ResolvedHolding => r !== null);

export const resolved = createMemo<ResolvedHolding[]>(() => accounts().flatMap(resolveAccount));

/**
 * Which of your accounts hold this fund, in the order the accounts are in.
 *
 * Answers a question every search on this app raises and none of them used to
 * answer: whether the row you are looking at is already yours. Reading a
 * screener with no idea which of the eighteen you own turns every comparison
 * into a memory test.
 *
 * The accounts, not a boolean, because the same fund in a TFSA and in a margin
 * account is a different position with a different tax treatment — and naming
 * them costs nothing when the caller only wanted to know whether the list is
 * empty.
 */
export const heldIn = (symbol: string): Account[] => accounts().filter((a) => (positionAt(a.id, symbol)?.units ?? 0) > 0);

// --- account rollups --------------------------------------------------------

/**
 * Uninvested cash: everything that has gone into the account less everything
 * spent out of it. Money in is deposits plus any loan balance allocated here;
 * money out is the all-in cost of the buys. Derived rather than stored, so it
 * cannot disagree with the blotter.
 *
 * Goes negative if buys exceed what was put in — a margin debit, and a real
 * thing to show rather than clamp.
 */
export const accountCash = (a: Account): number =>
    // Only cash-funded buys draw it down. A loan-funded buy was paid for by the
    // lender and never touched this account's cash — but the proceeds of selling
    // that same position do land here, because the broker credits the account
    // whether or not the loan is then paid down. See cashFlow.
    //
    // Distributions are cash in, and were missing from this for as long as it
    // existed. A book that had been paid $1,261 read $1,261 poorer than the
    // broker did, and deposits plus total return did not come to what the app
    // said the account was worth — which is the arithmetic anyone actually does.
    //
    // Right in both directions. Take the cash and it sits here until it is
    // spent; reinvest it and the DRIP buy in the blotter spends it, where before
    // that buy drew down money the app had never credited.
    a.deposit + receivedIn(a.id) + transactions.reduce((sum, t) => (t.accountId === a.id ? sum + cashFlow(t) : sum), 0);

export const accountValue = (a: Account): number | null => {
    const positions = sumOrNull(resolveAccount(a).map((r) => r.value));
    return positions == null ? null : accountCash(a) + positions;
};
export const accountIncome = (a: Account): number | null => sumOrNull(resolveAccount(a).map((r) => r.income));
export const accountBookCost = (a: Account): number => accountHoldings(a).reduce((sum, h) => sum + h.bookCost, 0);

/**
 * Gains and losses already crystallised here: what the sales fetched, less the
 * cost base they relieved.
 *
 * Against book cost rather than the return-of-capital-adjusted base, same as the
 * unrealised figure beside it. RoC lowers the real cost base and so raises the
 * real gain — by a lot, for this pool — and acb-store carries that per position;
 * it is applied where both halves are already loaded rather than here, which
 * would close an import cycle. The panel shows both numbers for that reason.
 */
export const accountRealised = (a: Account): number => realised({ accountId: a.id });

/** Everything a position's loans still have in it. */
const borrowedIn = (p: { loanCost: Map<string, number> }): number => [...p.loanCost.values()].reduce((sum, c) => sum + c, 0);

/**
 * What is held in this account on borrowed money, at cost.
 *
 * Read off the blotter — a buy names the loan that paid for it — so this cannot
 * disagree with the loan's own figures, which come from the same rows the other
 * way round.
 *
 * Of what is *still held*, not of everything ever bought. A sale relieves the
 * borrowed share of the cost base along with the rest of it, pro rata, so
 * liquidating a HELOC-funded account brings this back to zero rather than
 * leaving it reporting the account as fully borrowed with nothing in it.
 *
 * It matters beyond curiosity: interest is only deductible where the borrowed
 * money went into a taxable account, so the share of a registered account funded
 * by a loan is the share of that interest that buys you nothing back.
 */
export const accountLoanFunded = (a: Account): number => positionsIn(a.id).reduce((sum, p) => sum + borrowedIn(p), 0);

/**
 * Your own money in this account: what you put in.
 *
 * Deposits, or the cash actually spent on buys if that is larger — someone who
 * recorded the trades but never got round to the deposit box plainly had the
 * money, and taking the box at face value there would report their cash-funded
 * position as borrowed.
 */
export const accountOwnFunded = (a: Account): number => Math.max(a.deposit, accountBookCost(a) - accountLoanFunded(a));

/**
 * Which loans paid for something in this account, and how much each put in.
 *
 * Named rather than counted: "32% borrowed" is a different fact from "funded
 * with the HELOC", and the second is the one that decides whether the interest
 * is deductible, what rate it costs, and which balance to look at when the
 * number surprises you.
 */
export const accountLoans = (a: Account): { loan: Loan; cost: number }[] =>
    loans()
        .map((loan) => ({ loan, cost: allocatedTo(loan, a.id) }))
        .filter((row) => row.cost > 0)
        .sort((x, y) => y.cost - x.cost);

/**
 * The share of the money in this account that was borrowed, in percent.
 *
 * Against everything that went in — deposits and borrowing — not against what
 * has been invested. Measured on the invested amount, an account holding
 * $52,000 of uninvested cash and $57,000 of loan-funded stock reads as 100%
 * borrowed, which is the opposite of what its owner would say about it.
 *
 * Null with nothing in it at all, rather than 0%: an empty account is not a
 * cash-funded one.
 */
export const accountLoanShare = (a: Account): number | null => {
    const borrowed = accountLoanFunded(a);
    const total = accountOwnFunded(a) + borrowed;
    return total <= 0 ? null : (borrowed / total) * 100;
};
/**
 * Annual interest on what this account borrowed.
 *
 * Each funding loan's interest, apportioned by the share of that loan sitting in
 * this account. A HELOC split between a TFSA and a margin account costs both of
 * them something, and neither the whole bill nor none of it.
 */
export const accountInterest = (a: Account): number =>
    accountLoans(a).reduce((sum, row) => (row.loan.balance <= 0 ? sum : sum + loanInterest(row.loan) * Math.min(1, row.cost / row.loan.balance)), 0);

/** The blended rate on the borrowed money here — one figure for an account
    funded by two loans at different rates. Null with nothing borrowed. */
export const accountBorrowRate = (a: Account): number | null => {
    const borrowed = accountLoanFunded(a);
    return borrowed <= 0 ? null : (accountInterest(a) / borrowed) * 100;
};

/**
 * What the borrowed money in this account earns, per year.
 *
 * Not the account's income. Borrowed and own money bought the same fungible
 * units under one average cost, so the borrowed share of a position is the share
 * of its cost base the loan paid for — and the income that share earns is that
 * fraction of what the position pays. Taking the account's whole distribution
 * would credit the borrowing with money your own deposits bought.
 */
export const accountBorrowedIncome = (a: Account): number | null =>
    sumOrNull(
        positionsIn(a.id).map((p) => {
            if (p.bookCost <= 0) return 0;
            const annual = annualDistribution(p.symbol, todayISO());
            return annual == null ? null : annual * p.units * (borrowedIn(p) / p.bookCost);
        }),
    );

/** What that borrowed money yields, against what it bought. */
export const accountBorrowedYield = (a: Account): number | null => {
    const borrowed = accountLoanFunded(a);
    const income = accountBorrowedIncome(a);
    return borrowed <= 0 || income == null ? null : (income / borrowed) * 100;
};

/**
 * The spread the borrowing earns here: what it yields less what it costs.
 *
 * Positive is positive carry — these holdings pay for the loan and something
 * over. Negative means the position is being funded out of capital. Pre-tax,
 * like the book-level figure; deductibility widens it, and by how much is the
 * row below this one.
 */
export const accountSpread = (a: Account): number | null => {
    const earned = accountBorrowedYield(a);
    const rate = accountBorrowRate(a);
    return earned == null || rate == null ? null : earned - rate;
};

export const accountYield = (a: Account): number | null => {
    const value = accountValue(a);
    const income = accountIncome(a);
    return value == null || income == null || value <= 0 ? null : (income / value) * 100;
};

/** Lifetime room for this kind of account, or null where there is no cap. */
export const contributionLimit = (a: Account): number | null => CONTRIBUTION_LIMITS[a.kind] ?? null;

/** Room left against that cap: the limit less everything contributed so far.
    Null for an uncapped account; negative means over-contributed, which in a
    TFSA is penalised monthly. */
export const contributionRoom = (a: Account): number | null => {
    const limit = contributionLimit(a);
    return limit == null ? null : limit - a.deposit;
};

export const loanInterest = (l: Loan): number => (l.balance * l.rate) / 100;

/**
 * What a loan is still in, read off the blotter.
 *
 * These used to be declared in a `loan_allocations` table kept beside the buys,
 * which nothing reconciled: allocate twenty thousand to the TFSA, buy six, and
 * the app reported twenty invested. A buy now names the loan that paid for it,
 * so there is one record and it cannot disagree with itself.
 *
 * Still in, not ever put in: positions.ts carries each loan's share of a cost
 * base through disposals pro rata, so selling out of a loan-funded position
 * releases the loan from it here too.
 */
const fundedBy = (l: Loan): { position: Position; cost: number }[] =>
    [...positions().values()].map((position) => ({ position, cost: position.loanCost.get(l.id) ?? 0 })).filter((row) => row.cost > 0);

export const allocatedTo = (l: Loan, accountId: string): number =>
    fundedBy(l).reduce((sum, row) => (row.position.accountId === accountId ? sum + row.cost : sum), 0);

export const allocatedTotal = (l: Loan): number => fundedBy(l).reduce((sum, row) => sum + row.cost, 0);

/**
 * What this loan actually bought: the positions, not the account totals.
 *
 * Grouped by fund and account, because the same fund bought in two accounts on
 * one loan is two positions — they have different cost bases and only one of
 * them makes the interest deductible.
 */
export interface LoanPosition {
    symbol: string;
    accountId: string;
    units: number;
    cost: number;
}

/**
 * The units a loan's money accounts for.
 *
 * A position is not divided into borrowed units and cash units — they are the
 * same fungible units under one average cost — so the loan's share of it is the
 * share of its cost base the loan paid for. Twelve thousand of an eighteen
 * thousand position is two thirds of the units, whichever ones you point at.
 */
const unitsFor = (p: Position, cost: number): number => (p.bookCost <= 0 ? 0 : p.units * (cost / p.bookCost));

export const loanPositions = (l: Loan): LoanPosition[] =>
    fundedBy(l)
        .map(({ position, cost }) => ({ symbol: position.symbol, accountId: position.accountId, units: unitsFor(position, cost), cost }))
        .sort((a, b) => b.cost - a.cost);

/** What the positions this loan paid for actually pay, per year. Null only if a
    fund it bought has no distribution on file. */
export const loanIncome = (l: Loan): number | null =>
    sumOrNull(loanPositions(l).map((p) => {
        const annual = annualDistribution(p.symbol, todayISO());
        return annual == null ? null : p.units * annual;
    }));

/** Balance drawn but not yet spent. Negative means more has been bought on the
    loan than its balance covers, which is a data error worth seeing. */
export const unallocated = (l: Loan): number => l.balance - allocatedTotal(l);

/**
 * The fraction of a loan whose interest is deductible.
 *
 * Interest is deductible only where the borrowed money was used to earn taxable
 * income. A registered account earns tax-exempt income, so the part of a loan
 * that went into a TFSA or RRSP is not deductible, and neither is any part still
 * sitting uninvested. Split a loan across a margin account and a TFSA and only
 * the margin share counts — which is why this is a proportion, not a flag, and
 * why it is derived from the buys rather than stored anywhere.
 */
export const deductibleShare = (l: Loan): number => {
    if (l.balance <= 0) return 0;
    const taxable = new Set(accounts().filter((a) => !a.sheltered).map((a) => a.id));
    const intoTaxable = fundedBy(l).reduce((sum, row) => (taxable.has(row.position.accountId) ? sum + row.cost : sum), 0);
    return Math.min(1, Math.max(0, intoTaxable / l.balance));
};

export const loanDeductibleInterest = (l: Loan): number => loanInterest(l) * deductibleShare(l);

// --- portfolio totals -------------------------------------------------------

export const totalValue = createMemo(() => sumOrNull(accounts().map(accountValue)));
export const totalIncome = createMemo(() => sumOrNull(accounts().map(accountIncome)));
export const totalBookCost = createMemo(() => accounts().reduce((sum, a) => sum + accountBookCost(a), 0));
export const totalRealised = createMemo(() => accounts().reduce((sum, a) => sum + accountRealised(a), 0));
export const taxableIncome = createMemo(() => sumOrNull(accounts().filter((a) => !a.sheltered).map(accountIncome)));

export const totalDebt = createMemo(() => loans().reduce((sum, l) => sum + l.balance, 0));
export const totalInterest = createMemo(() => loans().reduce((sum, l) => sum + loanInterest(l), 0));
export const deductibleInterest = createMemo(() => loans().reduce((sum, l) => sum + loanDeductibleInterest(l), 0));

export const equity = createMemo(() => {
    const value = totalValue();
    return value == null ? null : value - totalDebt();
});
export const portfolioYield = createMemo(() => {
    const value = totalValue();
    const income = totalIncome();
    return value == null || income == null || value <= 0 ? null : (income / value) * 100;
});
export const monthlyIncome = createMemo(() => {
    const income = totalIncome();
    return income == null ? null : income / 12;
});

/** Weighted average rate across the loans, by balance — the one number that
    stands for "what this book borrows at" when it borrows at several rates. */
export const borrowRate = createMemo(() => {
    const balance = totalDebt();
    if (balance <= 0) return null;
    return loans().reduce((sum, l) => sum + l.balance * l.rate, 0) / balance;
});

/**
 * What the borrowed money itself earns — the positions it paid for, over the
 * amount borrowed.
 *
 * Not the book's yield. A buy names the loan that funded it, so which holdings
 * the borrowing bought is recorded rather than assumed, and assuming was wrong
 * in a way you could see: draw forty thousand, buy nothing with it, and the
 * average-yield version reported a healthy spread on money sitting in cash.
 * Money drawn and not spent earns nothing, and now says so.
 */
/** What the positions the borrowing paid for actually pay, per year. The
    numerator behind both the yield and the carry, named once so the two cannot
    be computed from different sets of loans. */
export const borrowedIncome = createMemo(() => sumOrNull(loans().map(loanIncome)));

export const borrowedYield = createMemo(() => {
    const debt = totalDebt();
    if (debt <= 0) return null;
    const income = borrowedIncome();
    return income == null ? null : (income / debt) * 100;
});

/**
 * Portfolio yield less the cost of borrowing: the spread the leverage earns.
 *
 * Positive is positive carry — the holdings pay for the loan and something over.
 * Negative is negative carry, where the borrowing costs more than the assets
 * yield and the position is being funded out of capital. Debt alone says what is
 * owed; this says whether owing it is working.
 *
 * Null with no debt rather than zero. Zero spread is a real state — borrowing at
 * exactly the yield — and it is not the same as not borrowing.
 *
 * Pre-tax, like everything else here. Deductibility would widen it, but by how
 * much depends on the marginal rate, and only the share invested in a taxable
 * account qualifies at all — that share is on the Loans page.
 */
export const spread = createMemo(() => {
    const earned = borrowedYield();
    const rate = borrowRate();
    return earned == null || rate == null ? null : earned - rate;
});

/**
 * What the borrowing actually nets, in dollars per month.
 *
 * The rate gap is the honest measure of whether leverage is working, but it is
 * not the one you can spend: 1.71% says nothing about whether that is worth the
 * risk until you know it is eleven dollars.
 *
 * Pre-tax. Deductibility makes the real figure better, by an amount that depends
 * on a marginal rate this app deliberately does not guess at.
 */
export const carryPerMonth = createMemo(() => {
    if (totalDebt() <= 0) return null;
    const income = borrowedIncome();
    return income == null ? null : (income - totalInterest()) / 12;
});
export const totalDeposits = createMemo(() => accounts().reduce((sum, a) => sum + a.deposit, 0));
export const totalCash = createMemo(() => accounts().reduce((sum, a) => sum + accountCash(a), 0));

/** Debt as a share of market value — the leverage the loans section implies. */
export const leverage = createMemo(() => {
    const value = totalValue();
    return value == null || value <= 0 ? null : (totalDebt() / value) * 100;
});

/** Symbols held that have no price yet — what is standing between the app and
    a real market value. */
export const unpricedSymbols = createMemo(() => [...new Set(resolved().filter((r) => !isPriced(r.symbol, todayISO())).map((r) => r.symbol))].sort());

// --- turning a placeholder into a row ---------------------------------------

/**
 * In-flight materialisations, keyed by placeholder.
 *
 * Typing in a deposit box fires on every keystroke, and the first two keystrokes
 * would otherwise each ask for the row to be created. The database would survive
 * that — ensure_account is get-or-create — but the second call would return an
 * id the store had already replaced, so this keeps one promise per placeholder
 * and hands the same one to every caller.
 */
const pending = new Map<string, Promise<string>>();

/** Replace a placeholder id everywhere it appears, so subsequent writes address
    the row directly and the UI's keys do not jump. */
const adopt = (from: string, to: string): void => {
    setPortfolios(
        produce((list: Portfolio[]) => {
            for (const p of list) {
                if (p.id === from) p.id = to;
                for (const a of p.accounts) if (a.id === from) a.id = to;
            }
        }),
    );
};

/**
 * The row id for an account or loan the app has been rendering from constants,
 * creating it if this is the first write into it.
 *
 * Returns the id unchanged when it is already a uuid, which is the common case —
 * the cost of laziness is one extra round trip per account, once ever.
 */
const materialise = async (id: string): Promise<string> => {
    if (!isPlaceholder(id)) return id;

    const existing = pending.get(id);
    if (existing) return existing;

    const { owner, sort, kind } = parsePlaceholder(id);
    // A slot name means the portfolio may not exist either, and ensure_* creates
    // it; a uuid owner is a plan, which always does.
    const isSlot = SLOTS.some((s) => s.slot === owner);
    const where = { p_portfolio: isSlot ? null : owner, p_slot: isSlot ? owner : null };

    const run = (async (): Promise<string> => {
        // Loans are never placeholders any more — they are rows the moment you
        // add one — so `sort` can only be an account here.
        const shape = ACCOUNT_SHAPE.find((s) => s.kind === kind);
        if (!shape) throw new Error(`unknown account kind: ${kind}`);
        const res = await db.rpc('ensure_account', {
            ...where,
            p_kind: shape.kind,
            p_label: shape.label,
            p_sheltered: shape.sheltered,
            p_position: ACCOUNT_SHAPE.indexOf(shape),
        });
        if (res.error) throw new Error(res.error.message);
        return String(res.data);
    })();

    pending.set(id, run);
    try {
        const real = await run;
        adopt(id, real);
        return real;
    } finally {
        pending.delete(id);
    }
};

/** The id of an account row, for callers outside this module that need one before
    they can write — adding a buy, most of all. */
export const accountRowId = (accountId: string): Promise<string> => materialise(accountId);

/** The same, for the loan that paid for a buy. A loan with no balance set yet
    has no row either, and naming it on a trade is a write into it. */
export const loanRowId = (loanId: string): Promise<string> => Promise.resolve(loanId);

// --- writers ----------------------------------------------------------------

const activeIndex = () => portfolios.findIndex((p) => p.id === activeId());

/**
 * Every writer below follows the same shape: update the store now so typing
 * stays instant, then persist against the row — materialising it first if this
 * is the write that brings it into existence.
 *
 * The debounce is keyed on the id the caller passed, placeholder or not, so that
 * repeated edits to the same box still collapse into one write while the row is
 * being created.
 */
const persist = (key: string, id: string, write: (rowId: string) => PromiseLike<unknown>): void => {
    debouncedWrite(key, async () => {
        try {
            return await write(await materialise(id));
        } catch (e) {
            reportError('saving', e);
            return undefined;
        }
    });
};

export const setAccountDeposit = (accountId: string, deposit: number): void => {
    const next = Math.max(0, deposit);
    setPortfolios(
        activeIndex(),
        produce((p: Portfolio) => {
            const account = p.accounts.find((a) => a.id === accountId);
            if (account) account.deposit = next;
        }),
    );
    persist(`deposit:${accountId}`, accountId, (id) => db.from('accounts').update({ deposit: next }).eq('id', id));
};


// --- loans ------------------------------------------------------------------
//
// Plain rows, unlike accounts: nothing is rendered before it exists, so there is
// no placeholder to materialise and no ensure_* to call. You add a loan and it
// is written; you delete it and it is gone.

/** The columns behind each editable field, so one writer serves them all. */
const LOAN_COLUMN: Record<'kind' | 'label' | 'issuer' | 'balance' | 'limit' | 'rate', string> = {
    kind: 'kind',
    label: 'label',
    issuer: 'issuer',
    balance: 'balance',
    limit: 'credit_limit',
    rate: 'rate',
};

/**
 * Correct one field of one loan.
 *
 * A field at a time on a debounce keyed to the row and the column, so typing
 * into a rate stays instant and only the last value is written. The store moves
 * first and the database after: a failed write surfaces as an error rather than
 * silently reverting what you typed, which is how an edit gets lost without
 * anyone noticing.
 */
export const setLoanField = <K extends keyof typeof LOAN_COLUMN>(loanId: string, field: K, value: Loan[K]): void => {
    const at = loanRows.findIndex((l) => l.id === loanId);
    if (at === -1) return;
    setLoanRows(at, field, value as never);

    const column = LOAN_COLUMN[field];
    debouncedWrite(`loan:${loanId}:${column}`, async () => {
        const { error: failed } = await db.from('loans').update({ [column]: value }).eq('id', loanId);
        if (failed) throw new Error(failed.message);
    });
};

export const setLoanBalance = (loanId: string, balance: number): void => setLoanField(loanId, 'balance', Math.max(0, balance));
export const setLoanRate = (loanId: string, rate: number): void => setLoanField(loanId, 'rate', Math.max(0, rate));
export const setLoanLimit = (loanId: string, limit: number | null): void => setLoanField(loanId, 'limit', limit == null ? null : Math.max(0, limit));

/**
 * Add a facility.
 *
 * Written through rather than optimistically: the row on screen is the row the
 * database returned, id and all, because a trade will name that id and an
 * invented one would fail the foreign key on the first buy.
 */
export const addLoan = async (kind: Loan['kind'] = 'heloc'): Promise<string | null> => {
    try {
        const res = await db
            .from('loans')
            // `user_id` is not sent: the column defaults to auth.uid(), so the
            // database decides whose row this is rather than the browser
            // claiming.
            .insert({
                id: crypto.randomUUID(),
                kind,
                label: LOAN_KIND_LABEL[kind],
                issuer: '',
                balance: 0,
                rate: DEFAULT_LOAN_RATE,
                position: loanRows.length,
            })
            .select()
            .single();
        if (res.error) throw new Error(res.error.message);
        const r = res.data as Record<string, unknown>;
        const saved: Loan = {
            id: String(r.id),
            kind: String(r.kind) as Loan['kind'],
            label: String(r.label),
            issuer: r.issuer == null ? '' : String(r.issuer),
            balance: Number(r.balance),
            limit: r.credit_limit == null ? null : Number(r.credit_limit),
            rate: Number(r.rate),
        };
        setLoanRows(produce((list) => void list.push(saved)));
        return saved.id;
    } catch (e) {
        reportError('adding loan', e);
        return null;
    }
};

/**
 * Remove a facility.
 *
 * The trades it funded are not removed with it — `on delete set null` — so the
 * positions it bought stay held and keep their cost base. What goes is the
 * record of how they were paid for, and with it the deductibility that record
 * supported. That is why the caller asks first.
 */
export const removeLoan = async (loanId: string): Promise<void> => {
    try {
        const res = await db.from('loans').delete().eq('id', loanId);
        if (res.error) throw new Error(res.error.message);
        setLoanRows(
            produce((list) => {
                const at = list.findIndex((l) => l.id === loanId);
                if (at >= 0) list.splice(at, 1);
            }),
        );
    } catch (e) {
        reportError('deleting loan', e);
    }
};


/** Throw away local state and re-read the database. */
export const reload = (): Promise<void> => load();

/**
 * A subscription owns exactly one real book — a TFSA and an RRSP are accounts
 * inside it, not portfolios beside it — so there is nothing to pick between and
 * no portfolio selector anywhere in the app.
 *
 * The other rows in the table are workspaces, and each is reached by going to
 * its own page rather than by putting the app into a mode:
 *
 *   main      the real book. Accounts, Loans, Payments and Project are always
 *             this and only this.
 *   the rest  plans, created and deleted from the Plan page.
 *
 * There is no third kind. A sandbox is just a plan nobody named — it had its own
 * slot, its own page and its own branch in the routing effect to be exactly a
 * plan that could not be renamed or deleted, which is less than a plan rather
 * than something else.
 *
 * Which one a portfolio is comes from `slot`, not from its id. It used to be
 * matched on id against the constant 'main', which was true of the SQLite schema
 * and stopped being true the moment ids became generated uuids.
 */
export const realPortfolio = createMemo<Portfolio | undefined>(() => portfolios.find((p) => p.slot === 'main'));

/** Everything that is not the real book. A row still carrying the retired
    'sandbox' slot lands here too, which is the point — it is a plan now. */
export const plans = createMemo<Portfolio[]>(() => portfolios.filter((p) => p.slot !== 'main'));

const [selectedPlanId, setSelectedPlanId] = createSignal('');
export { selectedPlanId, setSelectedPlanId };

/** The plan the Plan page is editing. Falls through to the first one so the page
    is never blank while a stale id sits in the signal. */
export const selectedPlan = createMemo<Portfolio | undefined>(() => plans().find((p) => p.id === selectedPlanId()) ?? plans()[0]);

const [savingPlan, setSavingPlan] = createSignal(false);
export { savingPlan };

/**
 * A new plan opens with the same accounts and loans as the real book, so planning
 * a position in an RRSP does not first require inventing somewhere to put it.
 *
 * One insert, because that is all a plan is now. There was a `create_plan`
 * function here whose whole job was to write the portfolio and its four accounts
 * in one transaction; accounts appear on first use, so there is nothing to keep
 * together and nothing for a function to do.
 */
export const createPlan = async (): Promise<void> => {
    if (savingPlan()) return;
    setSavingPlan(true);
    try {
        const taken = new Set(plans().map((p) => p.label));
        let n = plans().length + 1;
        while (taken.has(`Plan ${n}`)) n += 1;

        const res = await db
            .from('portfolios')
            .insert({ label: `Plan ${n}`, position: SLOTS.length + plans().length })
            .select('id')
            .single();
        if (res.error) throw new Error(res.error.message);
        await load();
        setSelectedPlanId(String(res.data.id));
    } catch (e) {
        reportError('creating plan', e);
    } finally {
        setSavingPlan(false);
    }
};

export const deletePlan = async (id: string): Promise<void> => {
    if (savingPlan() || !plans().some((p) => p.id === id)) return;
    setSavingPlan(true);
    try {
        // One statement: accounts cascade from the portfolio, and transactions
        // and loan allocations cascade from accounts. The SQLite schema had no
        // such key, which is why this used to delete each level by hand.
        const res = await db.from('portfolios').delete().eq('id', id);
        if (res.error) throw new Error(res.error.message);
        setSelectedPlanId('');
        await Promise.all([load(), loadTransactions()]);
    } catch (e) {
        reportError('deleting plan', e);
    } finally {
        setSavingPlan(false);
    }
};

export const renamePlan = (id: string, label: string): void => {
    const index = portfolios.findIndex((p) => p.id === id);
    if (index < 0) return;
    setPortfolios(index, 'label', label);
    debouncedWrite(`plan-label-${id}`, () => db.from('portfolios').update({ label }).eq('id', id));
};

const [copying, setCopying] = createSignal(false);
export { copying };

/**
 * Replace one portfolio's contents with another's — accounts, loans, loan
 * allocations and the whole blotter.
 *
 * Destructive: everything currently in the target is deleted first. It all goes
 * in one write batch, so a failure part-way leaves the target as it was rather
 * than half-copied.
 *
 * Ids are regenerated rather than reused. Accounts are keyed by id, so copying
 * them verbatim would make the two portfolios share rows and edits to one would
 * silently show up in the other.
 */
export const copyPortfolio = async (sourceId: string, targetId: string): Promise<void> => {
    if (sourceId === targetId || copying()) return;
    const source = portfolios.find((p) => p.id === sourceId);
    if (!source || !portfolios.some((p) => p.id === targetId)) return;

    // A slot nothing has ever been written into has no row to copy from. Said
    // rather than skipped: a button that quietly does nothing is worse than one
    // that explains why. The target is always a real plan, since a plan is only
    // reachable once it has been created.
    if (isPlaceholder(sourceId)) {
        reportError('copying portfolio', new Error(`${source.label} is empty — there is nothing to copy yet`));
        return;
    }

    setCopying(true);
    try {
        // One call, one transaction. The old version built two dozen statements
        // here and relied on the client getting the delete order right; the
        // function does the same work where a half-finished copy cannot be
        // observed, and where the foreign keys clear the target for it.
        const res = await db.rpc('copy_into_plan', { p_source: sourceId, p_target: targetId });
        if (res.error) throw new Error(res.error.message);
        await Promise.all([load(), loadTransactions()]);
    } catch (e) {
        reportError('copying portfolio', e);
    } finally {
        setCopying(false);
    }
};
